import React, { useState } from "react";
import styled from "styled-components";
import { defineMessages, injectIntl } from "react-intl";
import { FormattedMessage } from "components/FormattedMessage";
import Bugsnag from "@bugsnag/js";
import { FormStatus } from "./StyledDownloadSMSForm/FormStatus";
import { FormButton } from "./StyledDownloadSMSForm/FormButton";
import { sendSMSMessage } from "./StyledDownloadSMSForm/sendSMSMessage";

const messages = defineMessages({
  download_sms_cta: {
    id: "shared.DownloadSMSForm.sms_text",
    defaultMessage:
      "Book, manage tasks, message, and send photos to your Tasker " +
      'faster than you can say "I\'ll do it later." Enter your mobile number and we’ll send you a link to download the TaskRabbit app.',
  },
  input_placeholder: {
    id: "shared.DownloadSMSForm.sms_input_placeholder",
    defaultMessage: "Enter your mobile number",
  },
  input_label: {
    id: "shared.DownloadSMSForm.sms_input_label",
    defaultMessage: "Mobile phone number",
  },
});

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function DownloadSMSForm({ intl: { formatMessage } }) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const handleSMSDeliverySuccess = () => {
    setIsLoading(false);
    setHasError(false);
    setIsSuccessful(true);
  };

  const handleSMSDeliveryFailure = (error) => {
    setIsLoading(false);
    setHasError(true);
    setIsSuccessful(false);
    Bugsnag.notify(error);
  };

  const sendSMS = () => {
    setIsLoading(true);
    setHasError(false);
    setIsSuccessful(false);

    const postPromise = sendSMSMessage(phoneNumber);
    const minimumDelayPromise = delay(1000);

    postPromise
      .then(() => {
        minimumDelayPromise.then(handleSMSDeliverySuccess);
      })
      .catch(() => {
        minimumDelayPromise.then(handleSMSDeliveryFailure);
      });
  };

  const resetStates = () => {
    setIsLoading(false);
    setHasError(false);
    setIsSuccessful(false);
    setPhoneNumber("");
  };

  const handleChange = (e) => {
    setPhoneNumber(e.target.value);
  };

  return (
    <Container>
      <Title>
        <FormattedMessage message={messages.download_sms_cta} />
      </Title>
      <Form>
        <Input
          aria-label={formatMessage(messages.input_label)}
          placeholder={formatMessage(messages.input_placeholder)}
          type="text"
          value={phoneNumber}
          onClick={resetStates}
          onChange={handleChange}
        />
        <FormButton isLoading={isLoading} onClick={sendSMS} />
      </Form>
      <FormStatus
        isLoading={isLoading}
        isSuccessful={isSuccessful}
        hasError={hasError}
      />
    </Container>
  );
}

const Container = styled.div`
  margin-top: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const Title = styled.h3`
  font-size: ${(props) => props.theme.typography.typeScale[100]};
  font-weight: ${(props) => props.theme.typography.weight.regular};
  color: ${(props) => props.theme.color.textLabelValue};
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => props.theme.spacing.md};
  ${(props) => props.theme.breakpoints.tabletUp} {
    flex-direction: row;
  }
`;

const Input = styled.input`
  margin-bottom: ${(props) => props.theme.spacing.md};
  padding-top: ${(props) => props.theme.spacing.sm};
  padding-bottom: ${(props) => props.theme.spacing.sm};
  padding-left: ${(props) => props.theme.spacing.md};
  padding-right: ${(props) => props.theme.spacing.md};
  border: 1px solid ${(props) => props.theme.color.inputBorder};
  border-radius: ${(props) => props.theme.layout.borderRadius.md};
  font-weight: ${(props) => props.theme.typography.weight.semiBold};
  color: ${(props) => props.theme.color.textLabelValue};
  ${(props) => props.theme.breakpoints.tabletUp} {
    flex-grow: 2;
    margin-right: ${(props) => props.theme.spacing.sm};
  }
`;

export const StyledDownloadSMSForm = injectIntl(DownloadSMSForm);
